import { MARKET_DATA_MARKETS, SYMBOL_INFO_CACHE_VERSION } from './market-data.constants';
import { Symbol } from '../../models/symbol.model';
import {
	CachedSymbolInfo,
	LiveLastPrice,
	MarketDataMarket,
	MarketViewPriceLevel,
	MarketViewSnapshot,
	ResolvedLastPrice,
	SymbolInfo
} from './market-data.model';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNumber(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value);
}

function isNullableNumber(value: unknown): value is number | null {
	return value === null || isNumber(value);
}

function isOptionalNumber(value: unknown): value is number | null | undefined {
	return value === undefined || isNullableNumber(value);
}

function isNullableString(value: unknown): value is string | null {
	return value === null || typeof value === 'string';
}

function isMarket(value: unknown): value is MarketDataMarket {
	return MARKET_DATA_MARKETS.includes(value as MarketDataMarket);
}

function isSymbolInfoRow(value: unknown): value is Omit<SymbolInfo, 'isin'> & { isin: string | null } {
	return (
		isRecord(value) &&
		isNumber(value['contractId']) &&
		isNullableString(value['isin']) &&
		isNullableString(value['symbol']) &&
		isNullableString(value['symbolName']) &&
		isNullableString(value['shortName']) &&
		isNumber(value['commodityId']) &&
		isMarket(value['market']) &&
		typeof value['isActive'] === 'boolean' &&
		isNumber(value['tickSize']) &&
		isNumber(value['lot']) &&
		isNumber(value['maxOrderSize']) &&
		isNumber(value['commission']) &&
		isNumber(value['buyCommission']) &&
		isNumber(value['sellCommission']) &&
		isNullableNumber(value['marketGroupId']) &&
		isNullableString(value['imeDetailPageUrl'])
	);
}

function toSymbolInfo(row: Omit<SymbolInfo, 'isin'>, isin: string): SymbolInfo {
	return {
		contractId: row.contractId,
		isin,
		symbol: row.symbol,
		symbolName: row.symbolName,
		shortName: row.shortName,
		commodityId: row.commodityId,
		market: row.market,
		isActive: row.isActive,
		tickSize: row.tickSize,
		lot: row.lot,
		maxOrderSize: row.maxOrderSize,
		commission: row.commission,
		buyCommission: row.buyCommission,
		sellCommission: row.sellCommission,
		marketGroupId: row.marketGroupId,
		imeDetailPageUrl: row.imeDetailPageUrl
	};
}

export function parseSymbolInfoList(value: unknown): SymbolInfo[] | null {
	if (!Array.isArray(value) || !value.every(isSymbolInfoRow)) {
		return null;
	}

	return value.flatMap(row => (row.isin ? [toSymbolInfo(row, row.isin)] : []));
}

export function isCachedSymbolInfo(value: unknown): value is CachedSymbolInfo {
	return (
		isRecord(value) &&
		value['version'] === SYMBOL_INFO_CACHE_VERSION &&
		typeof value['savedAt'] === 'string' &&
		Array.isArray(value['data']) &&
		value['data'].every(row => isSymbolInfoRow(row) && typeof row.isin === 'string' && row.isin !== '')
	);
}

function isPriceLevel(value: unknown): value is MarketViewPriceLevel {
	return isRecord(value) && isNumber(value['level']) && isNumber(value['price']) && isNumber(value['volume']);
}

function isPriceLevelList(value: unknown): value is MarketViewPriceLevel[] {
	return Array.isArray(value) && value.every(isPriceLevel);
}

function isMarketViewRow(value: unknown): value is MarketViewSnapshot {
	return (
		isRecord(value) &&
		isNumber(value['contractId']) &&
		isNumber(value['lastSettlementPrice']) &&
		isPriceLevelList(value['bids']) &&
		isPriceLevelList(value['asks']) &&
		isNumber(value['firstTradedPrice']) &&
		isNumber(value['lastTradedPrice']) &&
		isNullableString(value['lastTradedPriceTime']) &&
		isNumber(value['highTradedPrice']) &&
		isNumber(value['lowTradedPrice']) &&
		isNumber(value['tradesCount']) &&
		isNumber(value['tradesVolume']) &&
		isNumber(value['tradesValue']) &&
		isNumber(value['openInterests']) &&
		isNumber(value['openInterestsChanges']) &&
		isOptionalNumber(value['closingPrice']) &&
		isOptionalNumber(value['lastPriceChange']) &&
		isOptionalNumber(value['highThreshold']) &&
		isOptionalNumber(value['lowThreshold'])
	);
}

export function parseMarketViewList(value: unknown): MarketViewSnapshot[] {
	return Array.isArray(value) ? value.filter(isMarketViewRow) : [];
}

/**
 * The first figure that is a real price. A feed reports "no price" as `0` as often as it
 * leaves the field out, and `??` only skips the latter, so a `0` tick used to win over the
 * last known price. 0 only when no candidate has one.
 */
export function firstPositivePrice(...candidates: (number | null | undefined)[]): number {
	return candidates.find((price): price is number => typeof price === 'number' && price > 0) ?? 0;
}

/**
 * One contract as the app reads it. A contract with no `market-view` row has not traded,
 * so its session figures are 0 — that is the data, not a gap to fill.
 */
export function toSymbol(info: SymbolInfo, quote: MarketViewSnapshot | undefined): Symbol {
	return {
		isin: info.isin,
		symbol: info.symbol ?? '',
		symbolName: info.symbolName ?? '',
		symbolShortName: info.shortName ?? '',
		contractId: info.contractId,
		commodityId: info.commodityId,
		market: info.market,
		marketGroupId: info.marketGroupId ?? 0,
		imeDetailPageUrl: info.imeDetailPageUrl ?? '',
		tickSize: info.tickSize,
		lot: info.lot,
		maxOrderSize: info.maxOrderSize,
		commission: info.commission,
		buyCommission: info.buyCommission,
		sellCommission: info.sellCommission,
		lastSettlementPrice: quote?.lastSettlementPrice ?? 0,
		firstTradedPrice: quote?.firstTradedPrice ?? 0,
		lastTradedPrice: firstPositivePrice(quote?.lastTradedPrice, quote?.closingPrice),
		lastTradedPriceTime: quote?.lastTradedPriceTime ?? null,
		closingPrice: firstPositivePrice(quote?.closingPrice, quote?.lastSettlementPrice),
		lastPriceChange: quote?.lastPriceChange ?? 0,
		highTradedPrice: quote?.highTradedPrice ?? 0,
		lowTradedPrice: quote?.lowTradedPrice ?? 0,
		tradesCount: quote?.tradesCount ?? 0,
		tradesVolume: quote?.tradesVolume ?? 0,
		tradesValue: quote?.tradesValue ?? 0,
		openInterests: quote?.openInterests ?? 0,
		openInterestsChanges: quote?.openInterestsChanges ?? 0,
		highThreshold: quote?.highThreshold ?? 0,
		lowThreshold: quote?.lowThreshold ?? 0,
		bids: quote?.bids ?? [],
		asks: quote?.asks ?? []
	};
}

/**
 * آخرین قیمت and its change for a screen holding a snapshot and maybe a live tick.
 *
 * A tick only counts when it carries a trade: before the open and after the close the feed
 * sends 0, which must neither blank the last price nor recompute a change the backend has
 * already zeroed for the night. Without such a tick the snapshot's own change is shown as sent.
 */
export function resolveLastPrice(
	live: LiveLastPrice | undefined,
	meta: Pick<Symbol, 'lastTradedPrice' | 'lastSettlementPrice' | 'lastPriceChange'> | undefined
): ResolvedLastPrice {
	const previousClose = firstPositivePrice(live?.lsp, meta?.lastSettlementPrice);
	const tickPrice = firstPositivePrice(live?.ltp);

	if (tickPrice) {
		return { lastPrice: tickPrice, lastPriceChange: previousClose ? tickPrice - previousClose : 0, previousClose };
	}

	return {
		lastPrice: firstPositivePrice(meta?.lastTradedPrice),
		lastPriceChange: meta?.lastPriceChange ?? 0,
		previousClose
	};
}
